import { Component, computed, HostListener, inject, Input, LOCALE_ID, OnInit, signal } from '@angular/core';
import { Router } from '@angular/router';
import { CategoryService } from '../../../../services/category/category.service';
import { CategoryVTO } from '../../../../pages/admin-dashboard/categories/category.model';
import { finalize } from 'rxjs';
import { SubCategory } from './view-category-details.model';
import { CategoryFileMgt } from '../../../../services/category/fileMgt.category.service';

@Component({
  selector: 'app-view-category-details',
  imports: [],
  templateUrl: './view-category-details.html',
  styleUrl: './view-category-details.scss',
})
export class ViewCategoryDetails implements OnInit {
  private locale = inject(LOCALE_ID);
  isArabic = computed(() => this.locale.startsWith('ar'));
  @Input() categoryId!: number;

  private categoryService = inject(CategoryService);
  private categoryFileMgt = inject(CategoryFileMgt);
  private router = inject(Router);
  imagePreviewUrl = signal<string>('');

  loading = signal<boolean>(false);
  loadingSubCategories = signal<boolean>(false);
  errorLoadingCategory = signal<string | null>(null);

  category: CategoryVTO | null = null;
  subCategories: SubCategory[] = [];
  currentPage = 1;
  itemsPerPage = 10;
  totalItems = 0;
  activeDropdownId = signal<number | null>(null);
  showCategoryDropdown = signal<boolean>(false);
  showDeleteDialog = false;
  showSuccessMessage = false;

  ngOnInit() {
    if (this.categoryId) {
      this.loadCategory();
      this.loadSubCategories();
    }
  }

  loadCategory() {
    this.loading.set(true);
    this.categoryService
      .getCategoryById(this.categoryId)
      .pipe(
        finalize(() => {
          this.loading.set(false);
        }),
      )
      .subscribe({
        next: (category: CategoryVTO) => {
          this.category = category;
          this.categoryFileMgt.getImage(category.profileImageFid!).subscribe({
            next: (blob: Blob) => {
              const imageUrl = window.URL.createObjectURL(blob);
              this.imagePreviewUrl.set(imageUrl);
            },
            error: () => {},
          });
        },
        error: (err) => {
          console.error('Error loading category:', err);
          this.errorLoadingCategory.set('Failed to load category. Please try again.');
        },
      });
  }

  loadSubCategories() {
    this.loadingSubCategories.set(true);
    setTimeout(() => {
      this.subCategories = [
        {
          id: 1,
          titleEn: 'Oral Painkillers',
          titleAr: 'مسكنات الألم الفموية',
          dateCreated: '2/6/2023',
          lastUpdated: '2/6/2023',
        },
        {
          id: 2,
          titleEn: 'Topical Pain Relief',
          titleAr: 'مسكنات الألم الموضعية',
          dateCreated: '2/6/2023',
          lastUpdated: '2/6/2023',
        },
        {
          id: 3,
          titleEn: 'Migraine Relief',
          titleAr: 'علاج الصداع النصفي',
          dateCreated: '2/6/2023',
          lastUpdated: '2/6/2023',
        },
        {
          id: 4,
          titleEn: 'Back & Joint Pain Relief',
          titleAr: 'علاج آلام الظهر والمفاصل',
          dateCreated: '2/6/2023',
          lastUpdated: '2/6/2023',
        },
        {
          id: 5,
          titleEn: 'Menstrual Pain Relief',
          titleAr: 'علاج آلام الدورة الشهرية',
          dateCreated: '2/6/2023',
          lastUpdated: '2/6/2023',
        },
        {
          id: 6,
          titleEn: 'Fever Reducers',
          titleAr: 'خافضات الحرارة',
          dateCreated: '2/6/2023',
          lastUpdated: '2/6/2023',
        },
        {
          id: 7,
          titleEn: 'Muscle Relaxants',
          titleAr: 'مرخيات العضلات',
          dateCreated: '2/6/2023',
          lastUpdated: '2/6/2023',
        },
      ];
      this.totalItems = this.subCategories.length;
      this.loadingSubCategories.set(false);
    }, 500);
  }

  get paginatedSubCategories(): SubCategory[] {
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;
    return this.subCategories.slice(startIndex, endIndex);
  }

  get totalPages(): number {
    return Math.ceil(this.totalItems / this.itemsPerPage);
  }

  get startItem(): number {
    return (this.currentPage - 1) * this.itemsPerPage + 1;
  }

  get endItem(): number {
    const end = this.currentPage * this.itemsPerPage;
    return end > this.totalItems ? this.totalItems : end;
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
    }
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
    }
  }

  previousPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
    }
  }

  getPageNumbers(): number[] {
    const pages: number[] = [];
    const maxPages = Math.min(this.totalPages, 100);
    for (let i = 1; i <= maxPages; i++) {
      pages.push(i);
    }
    return pages;
  }

  getCategoryImage(category: CategoryVTO | null): string {
    return 'https://placehold.co/400x400?text=No+Image';
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    this.activeDropdownId.set(null);
    this.showCategoryDropdown.set(false);
  }

  toggleDropdown(id: number, event: Event) {
    event.stopPropagation();
    if (this.activeDropdownId() == id) {
      this.activeDropdownId.set(null);
    } else {
      this.activeDropdownId.set(id);
    }
  }

  toggleCategoryDropdown(event: Event) {
    event.stopPropagation();
    this.showCategoryDropdown.set(!this.showCategoryDropdown());
  }

  handleCategoryAction(action: string) {
    if (action == 'Edit Category' && this.category) {
      this.router.navigate(['/admin/categories/edit-category', this.category.id]);
    } else if (action == 'Delete Category' && this.category) {
      this.openDeleteDialog();
    }
    this.showCategoryDropdown.set(false);
  }

  openDeleteDialog() {
    this.showDeleteDialog = true;
  }

  closeDeleteDialog() {
    this.showDeleteDialog = false;
  }

  deleteCategory() {
    if (this.category) {
      const deletedId = this.category.id;
      this.closeDeleteDialog();
      this.categoryService.deleteCategory(deletedId).subscribe({
        next: () => {
          this.showSuccessMessage = true;
          setTimeout(() => {
            this.showSuccessMessage = false;
            this.router.navigate(['/admin/categories']);
          }, 2000);
        },
        error: (error) => {
          console.error('Error deleting category:', error);
        },
      });
    }
  }

  handleAction(action: string, subCategory: SubCategory) {
    if (action == 'Edit Sub Category') {
      console.log('Edit sub category:', subCategory.id);
    } else if (action == 'Delete Sub Category') {
      console.log('Delete sub category:', subCategory.id);
    }
    this.activeDropdownId.set(null);
  }

  onAddSubCategory() {
    console.log('Add sub category clicked');
  }
}
